import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Send,
  Save,
  Trash2,
  LogOut,
  Plus,
  Users,
  Mail,
  ArrowLeft,
  Loader2,
  Check,
  AlertCircle,
} from "lucide-react";
import {
  adminLogin,
  getAdminAuthHeader,
  clearAdminAuth,
  listDrafts,
  createDraft,
  updateDraft,
  deleteDraft,
  sendDraft,
  listSubscribers,
} from "@/lib/adminApi";

function LoginScreen({ onSuccess }) {
  const [user, setUser] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await adminLogin(user, pw);
      onSuccess();
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Invalid credentials" : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login" className="min-h-screen bg-[#FAF7EC] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src="/logo.png" alt="MSWDC" className="h-20 w-auto mx-auto mb-6" />
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
            Coalition Admin
          </div>
          <h1 className="mt-3 font-display font-black text-3xl text-slate-900">
            Sign in
          </h1>
        </div>

        <form onSubmit={submit} className="border border-slate-200 bg-white p-8 space-y-6">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              Username
            </label>
            <input
              data-testid="admin-login-user"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              autoComplete="username"
              required
              className="mswdc-input"
              placeholder="jake@MSWDC"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              Password
            </label>
            <input
              data-testid="admin-login-pass"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="current-password"
              required
              className="mswdc-input"
            />
          </div>
          {err && (
            <p data-testid="admin-login-error" className="text-sm text-rose-700 flex items-center gap-2">
              <AlertCircle size={14} /> {err}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full bg-slate-900 hover:bg-amber-600 disabled:opacity-60 text-white px-6 py-3 font-semibold text-sm transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={14} className="animate-spin" /> Verifying</> : "Sign in"}
          </button>
        </form>

        <div className="mt-8 text-center">
          <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-slate-900">
            ← back to site
          </Link>
        </div>
      </div>
    </main>
  );
}

function NewDraftForm({ onClose, onCreated }) {
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const draft = await createDraft({ topic, notes: notes || undefined });
      onCreated(draft);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Draft generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="new-draft-form" className="border border-slate-200 bg-white">
      <div className="p-6 md:p-8 border-b border-slate-200 flex items-start justify-between gap-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
            Draft a new Monthly Briefing
          </div>
          <h2 className="mt-3 font-display font-black text-2xl md:text-3xl text-slate-900">
            What's this issue about?
          </h2>
          <p className="mt-2 text-slate-600 text-sm max-w-2xl">
            Give the AI a topic and any source material you want it to draw from. The draft
            will follow the doctrine voice automatically — you'll edit before sending.
          </p>
        </div>
        <button
          onClick={onClose}
          data-testid="new-draft-cancel"
          className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-slate-900"
        >
          cancel
        </button>
      </div>

      <form onSubmit={submit} className="p-6 md:p-8 space-y-6">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            Topic *
          </label>
          <input
            data-testid="new-draft-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            required
            minLength={2}
            maxLength={300}
            placeholder="e.g. Welfare in practice — a quiet week at a rural school"
            className="mswdc-input"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            Editor notes / source material
          </label>
          <textarea
            data-testid="new-draft-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            maxLength={4000}
            placeholder="Bullet points, quotes, names, recent fieldwork — anything the AI should weave in. Optional."
            className="mswdc-input resize-none"
          />
        </div>

        {err && (
          <p data-testid="new-draft-error" className="text-sm text-rose-700 flex items-center gap-2">
            <AlertCircle size={14} /> {err}
          </p>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
          <p className="text-xs text-slate-500">Draft generation usually takes 10–25 seconds.</p>
          <button
            type="submit"
            data-testid="new-draft-submit"
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white px-6 py-3 font-semibold text-sm flex items-center gap-2 transition-colors"
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Drafting…</>
            ) : (
              <><Sparkles size={14} /> Generate draft</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function DraftEditor({ draft, subscriberCount, onBack, onUpdated, onDeleted, onSent }) {
  const [subject, setSubject] = useState(draft.subject || "");
  const [body, setBody] = useState(draft.body_text || "");
  const [busy, setBusy] = useState(null); // 'save' | 'send' | 'delete' | null
  const [msg, setMsg] = useState({ kind: null, text: "" });

  useEffect(() => {
    setSubject(draft.subject || "");
    setBody(draft.body_text || "");
  }, [draft]);

  const dirty = subject !== draft.subject || body !== draft.body_text;
  const isSent = draft.status === "sent";

  const save = async () => {
    setBusy("save");
    setMsg({ kind: null, text: "" });
    try {
      const updated = await updateDraft(draft.id, { subject, body_text: body });
      onUpdated(updated);
      setMsg({ kind: "ok", text: "Saved." });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Save failed" });
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this draft? This cannot be undone.")) return;
    setBusy("delete");
    try {
      await deleteDraft(draft.id);
      onDeleted(draft.id);
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Delete failed" });
      setBusy(null);
    }
  };

  const send = async () => {
    if (dirty) {
      if (!window.confirm("You have unsaved edits. Save and send?")) return;
      await save();
    }
    if (!window.confirm(`Send this briefing to ${subscriberCount} subscriber${subscriberCount === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBusy("send");
    setMsg({ kind: null, text: "" });
    try {
      const updated = await sendDraft(draft.id);
      onSent(updated);
      setMsg({ kind: "ok", text: `Queued for delivery to ${updated.recipients_count} subscriber${updated.recipients_count === 1 ? "" : "s"}.` });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Send failed" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="draft-editor" className="border border-slate-200 bg-white">
      <div className="p-6 md:p-8 border-b border-slate-200 flex items-start justify-between gap-6">
        <div>
          <button
            onClick={onBack}
            data-testid="draft-back"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> back to drafts
          </button>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
            {isSent ? `Sent ${new Date(draft.sent_at).toLocaleString()}` : "Draft"} · {draft.topic}
          </div>
          <h2 className="mt-2 font-display font-black text-2xl md:text-3xl text-slate-900">
            {isSent ? "View sent briefing" : "Edit & send"}
          </h2>
        </div>
        {!isSent && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={remove}
              data-testid="draft-delete"
              disabled={!!busy}
              className="border border-slate-300 hover:border-rose-500 hover:text-rose-700 disabled:opacity-50 px-4 py-2 text-sm flex items-center gap-2 transition-colors"
            >
              <Trash2 size={14} /> Delete
            </button>
            <button
              onClick={save}
              data-testid="draft-save"
              disabled={!dirty || !!busy}
              className="border border-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-50 px-4 py-2 text-sm flex items-center gap-2 transition-colors"
            >
              {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
            <button
              onClick={send}
              data-testid="draft-send"
              disabled={!!busy || subscriberCount === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 px-4 py-2 text-sm flex items-center gap-2 transition-colors"
            >
              {busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send to {subscriberCount}
            </button>
          </div>
        )}
      </div>

      <div className="p-6 md:p-8 space-y-6">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            Subject line
          </label>
          <input
            data-testid="draft-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={isSent}
            className="mswdc-input"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            Body (plain text — paragraphs separated by blank lines)
          </label>
          <textarea
            data-testid="draft-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={isSent}
            rows={22}
            className="mswdc-input resize-y font-body"
            style={{ borderBottom: "1px solid #E2E8F0", padding: "14px", borderRadius: 0 }}
          />
        </div>

        {msg.text && (
          <p
            data-testid="draft-status-msg"
            className={`text-sm flex items-center gap-2 ${msg.kind === "ok" ? "text-emerald-700" : "text-rose-700"}`}
          >
            {msg.kind === "ok" ? <Check size={14} /> : <AlertCircle size={14} />} {msg.text}
          </p>
        )}
      </div>
    </div>
  );
}

function DraftsList({ drafts, subscriberCount, onOpen, onNew, onLogout }) {
  return (
    <div data-testid="drafts-list">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
            Coalition Admin · Newsletter
          </div>
          <h1 className="mt-3 font-display font-black text-3xl md:text-5xl tracking-tighter text-slate-900">
            The Monthly Briefing
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-5 text-sm text-slate-600">
            <span className="inline-flex items-center gap-2">
              <Users size={14} className="text-amber-600" />
              <strong className="text-slate-900">{subscriberCount}</strong> subscriber{subscriberCount === 1 ? "" : "s"}
            </span>
            <span className="inline-flex items-center gap-2">
              <Mail size={14} className="text-amber-600" />
              <strong className="text-slate-900">{drafts.length}</strong> draft{drafts.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onNew}
            data-testid="new-draft-button"
            className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-3 font-semibold text-sm flex items-center gap-2 transition-colors"
          >
            <Plus size={14} /> New draft
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="border border-slate-300 hover:border-slate-900 px-4 py-3 text-sm flex items-center gap-2 transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      {drafts.length === 0 ? (
        <div data-testid="drafts-empty" className="border border-dashed border-slate-300 p-12 text-center bg-white">
          <p className="text-slate-500">No drafts yet. Create your first briefing.</p>
        </div>
      ) : (
        <ul className="border-t border-slate-200">
          {drafts.map((d) => (
            <li
              key={d.id}
              data-testid={`draft-row-${d.id}`}
              className="border-b border-slate-200 hover:bg-amber-50/40 transition-colors"
            >
              <button
                onClick={() => onOpen(d)}
                className="w-full text-left p-5 md:p-6 flex items-center justify-between gap-6"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <span
                      className={`font-mono text-[10px] uppercase tracking-[0.22em] ${d.status === "sent" ? "text-emerald-700" : "text-amber-600"}`}
                    >
                      {d.status === "sent" ? `Sent · ${d.recipients_count}` : "Draft"}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                      {new Date(d.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="mt-1 font-display font-bold text-lg md:text-xl text-slate-900 truncate">
                    {d.subject || d.topic}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500 truncate">{d.topic}</p>
                </div>
                <ArrowLeft className="rotate-180 text-slate-400 shrink-0" size={18} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!getAdminAuthHeader());
  const [drafts, setDrafts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [view, setView] = useState("list"); // 'list' | 'new' | 'editor'
  const [active, setActive] = useState(null);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    document.title = "Admin · MSWDC";
  }, []);

  const refresh = async () => {
    setLoadErr("");
    try {
      const [d, s] = await Promise.all([listDrafts(), listSubscribers()]);
      setDrafts(d);
      setSubscribers(s);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminAuth();
        setAuthed(false);
      } else {
        setLoadErr(e?.message || "Could not load data");
      }
    }
  };

  useEffect(() => {
    if (authed) refresh();
  }, [authed]);

  const subscriberCount = subscribers.length;

  if (!authed) {
    return <LoginScreen onSuccess={() => setAuthed(true)} />;
  }

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#FAF7EC]">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12 py-10 md:py-16">
        {view === "list" && (
          <DraftsList
            drafts={drafts}
            subscriberCount={subscriberCount}
            onOpen={(d) => { setActive(d); setView("editor"); }}
            onNew={() => setView("new")}
            onLogout={() => { clearAdminAuth(); setAuthed(false); }}
          />
        )}

        {view === "new" && (
          <NewDraftForm
            onClose={() => setView("list")}
            onCreated={(d) => {
              setDrafts((prev) => [d, ...prev]);
              setActive(d);
              setView("editor");
            }}
          />
        )}

        {view === "editor" && active && (
          <DraftEditor
            draft={active}
            subscriberCount={subscriberCount}
            onBack={() => { setActive(null); setView("list"); refresh(); }}
            onUpdated={(d) => {
              setActive(d);
              setDrafts((prev) => prev.map((x) => (x.id === d.id ? d : x)));
            }}
            onDeleted={(id) => {
              setDrafts((prev) => prev.filter((x) => x.id !== id));
              setActive(null);
              setView("list");
            }}
            onSent={(d) => {
              setActive(d);
              setDrafts((prev) => prev.map((x) => (x.id === d.id ? d : x)));
            }}
          />
        )}

        {loadErr && (
          <p data-testid="admin-load-error" className="mt-6 text-sm text-rose-700">{loadErr}</p>
        )}
      </div>
    </main>
  );
}
